/**
 * DTO → view-model mapper for the `Person` resource.
 *
 * Stability contract (matches `currentUser.ts`):
 *   - Every snake_case → camelCase rename is explicit. No generic deep
 *     transform. If the OpenAPI contract adds or renames a field, this
 *     mapper must be updated deliberately so the rename shows up in
 *     review.
 *   - Unknown enum values are narrowed to the view model's documented
 *     fallback (``'U'`` for sex, ``'UNKNOWN'`` for life status, ``null``
 *     for contact kind) so the UI never has to reason about server-side
 *     enum drift.
 *   - Optional DTO fields become ``null`` in the view model — never
 *     ``undefined``. This keeps UI empty-state branches symmetric and
 *     JSON-serializable state predictable.
 */

import type {
  components,
  EthnicityDto,
  EthnicityTermDto,
  IdentifierDefinitionDto,
  LiveDemographicsBody,
  PatientPhysicalChartDto,
  PersonDto,
  PersonMetadataWithCareTeamDto,
  PersonSummaryDto,
} from '../client';
import type {
  AddressViewModel,
  AdoptedStatus,
  CareTeamMemberViewModel,
  CarrierStatus,
  ChildlessStatus,
  ContactKind,
  ContactPointViewModel,
  ContactUse,
  DemographicsViewModel,
  EthnicityType,
  EthnicityTermViewModel,
  EthnicityViewModel,
  InterpreterRequired,
  LifeStatus,
  PatientMetadataViewModel,
  PatientSummaryViewModel,
  PatientViewModel,
  PersonIdentifierViewModel,
  PhysicalChartViewModel,
  PregnancyDatingBasis,
  SensitivityViewModel,
  SexCode,
} from '@/models/patient';

import { identifierLabelFor } from '@/lib/identifierLabel';

import { narrowEnum, nullableString } from './_shared';

type DemographicsDto = components['schemas']['Demographics'];
type AddressDto = components['schemas']['Address'];
type ContactPointDto = components['schemas']['ContactPoint'];
type PersonIdentifierDto = components['schemas']['PersonIdentifier'];
type PersonMetadataDto = PersonMetadataWithCareTeamDto;

/** The live `Demographics` payload includes fields not yet in the checked-in
 *  OpenAPI snapshot (PTV2-1064: ethnicities, physical_chart, four scalars).
 *  Widen at the mapper boundary so reads stay typed end-to-end without
 *  forcing a contract regen for every BE addition. */
type LiveDemographicsRead = DemographicsDto & Partial<LiveDemographicsBody>;

const KNOWN_SEX: ReadonlySet<SexCode> = new Set<SexCode>(['M', 'F', 'O', 'U', 'INTERSEX', 'UNASSIGNED']);
const KNOWN_LIFE_STATUS: ReadonlySet<LifeStatus> = new Set<LifeStatus>([
  'ALIVE',
  'ALIVE_AND_WELL',
  'DECEASED',
  'UNBORN',
  'STILL_BORN',
  'MISCARRIAGE',
  'ABORTED',
  'UNKNOWN',
]);
const KNOWN_PREGNANCY_DATING_BASIS: ReadonlySet<PregnancyDatingBasis> =
  new Set<PregnancyDatingBasis>(['LMP', 'EDD', 'GA']);
const KNOWN_CONTACT_KIND: ReadonlySet<ContactKind> = new Set<ContactKind>([
  'EMAIL',
  'PHONE',
  'SMS',
]);
const KNOWN_CONTACT_USE: ReadonlySet<ContactUse> = new Set<ContactUse>([
  'HOME',
  'WORK',
  'MOBILE',
  'OLD',
  'TEMP',
]);
const KNOWN_INTERPRETER: ReadonlySet<InterpreterRequired> = new Set<InterpreterRequired>([
  'Y',
  'N',
  'U',
]);
const KNOWN_ETHNICITY_TYPE: ReadonlySet<EthnicityType> = new Set<EthnicityType>([
  'PATIENT',
  'PATERNAL',
  'MATERNAL',
]);

function mapAddress(dto: AddressDto): AddressViewModel {
  return {
    line: nullableString(dto.addressLine1),
    line2: nullableString(dto.addressLine2),
    city: nullableString(dto.city),
    district: nullableString(dto.district),
    province: nullableString(dto.province),
    postalCode: nullableString(dto.postalCode),
    country: nullableString(dto.country),
  };
}

function mapContactPoint(dto: ContactPointDto): ContactPointViewModel | null {
  const type = narrowEnum<ContactKind>(dto.type, KNOWN_CONTACT_KIND, null);
  if (type === null) return null;
  const value = nullableString(dto.value);
  if (value === null) return null;
  return {
    type,
    value,
    preferred: dto.preferred ?? false,
    use: narrowEnum<ContactUse>(dto.use, KNOWN_CONTACT_USE, null),
  };
}

function mapIdentifier(dto: PersonIdentifierDto): PersonIdentifierViewModel | null {
  const value = nullableString(dto.value);
  const identifierType = nullableString(dto.userCreatedId);
  if (value === null || identifierType === null) return null;
  return {
    id: typeof dto.id === 'number' ? String(dto.id) : null,
    identifierType,
    label: nullableString(dto.label),
    value,
  };
}

function readNamePart(
  dto: DemographicsDto,
  field: 'firstName' | 'lastName' | 'lastNameAtBirth',
): string | null {
  const names = dto.names;
  if (names === undefined || names.length === 0) return null;
  const first = names[0];
  if (first === undefined) return null;
  return nullableString(first[field]);
}

/**
 * Convert the BE's `{year, month?, day?}` partial-date triple to
 * `YYYY[-MM[-DD]]` so view-model consumers (`formatPartialDate`, etc.) get a
 * single string shape to format.
 */
function formatPartialDate(
  dob: NonNullable<DemographicsDto['dateOfBirth']> | undefined,
): string | null {
  if (dob === undefined || typeof dob.year !== 'number') return null;
  const parts = [String(dob.year).padStart(4, '0')];
  if (typeof dob.month === 'number') parts.push(String(dob.month).padStart(2, '0'));
  if (typeof dob.month === 'number' && typeof dob.day === 'number') {
    parts.push(String(dob.day).padStart(2, '0'));
  }
  return parts.join('-');
}

/** Word enums from OpenAPI plus single-letter codes some BE paths emit (`"f"` / `"m"`). */
const SEX_FROM_DTO: Readonly<Record<string, SexCode>> = {
  male: 'M',
  female: 'F',
  other: 'O',
  unknown: 'U',
  m: 'M',
  f: 'F',
  o: 'O',
  u: 'U',
  // PTV2-1332: assigned-at-birth-only codes the BE SexOptions accepts.
  intersex: 'INTERSEX',
  unassigned: 'UNASSIGNED',
};

function readSex(value: DemographicsDto['sex']): SexCode | null {
  if (value === undefined || value === null) return null;
  if (typeof value !== 'string') return null;
  const key = value.trim().toLowerCase();
  if (key.length === 0) return null;
  return SEX_FROM_DTO[key] ?? null;
}

function readLifeStatus(value: DemographicsDto['lifeStatus']): LifeStatus | null {
  if (value === undefined) return null;
  return narrowEnum<LifeStatus>(value.lifeStatusType, KNOWN_LIFE_STATUS, null);
}

/** BE-accepted `GenderOptions` codes. Lets us round-trip legacy free-text
 *  values (e.g. `"Female"`) into the new code shape (`"F"`) so they don't
 *  fail server validation on the next save. */
const GENDER_FROM_DTO: Readonly<Record<string, string>> = {
  m: 'M',
  male: 'M',
  f: 'F',
  female: 'F',
  o: 'O',
  other: 'O',
  nonbinary: 'nonBinary',
  'non-binary': 'nonBinary',
  unknown: 'unknown',
};

function readGenderIdentity(value: DemographicsDto['gender']): string | null {
  const raw = nullableString(value);
  if (raw === null) return null;
  const normalized = GENDER_FROM_DTO[raw.toLowerCase()];
  return normalized ?? raw;
}

function mapEthnicityTerm(dto: EthnicityTermDto): EthnicityTermViewModel | null {
  const label = nullableString(dto.label);
  if (label === null) return null;
  return { termId: nullableString(dto.termId), label };
}

function mapEthnicity(dto: EthnicityDto): EthnicityViewModel | null {
  const type = narrowEnum<EthnicityType>(dto.type, KNOWN_ETHNICITY_TYPE, null);
  if (type === null) return null;
  const terms: EthnicityTermViewModel[] = [];
  for (const raw of dto.terms ?? []) {
    const mapped = mapEthnicityTerm(raw);
    if (mapped !== null) terms.push(mapped);
  }
  return { id: nullableString(dto.id), type, terms };
}

function mapPhysicalChart(
  dto: PatientPhysicalChartDto | undefined | null,
): PhysicalChartViewModel | null {
  if (dto === undefined || dto === null) return null;
  return {
    id: nullableString(dto.id),
    date: nullableString(dto.date),
    location: nullableString(dto.location),
    staffMemberUserGuid: nullableString(dto.staff_member_user_guid),
    notes: nullableString(dto.notes),
  };
}

function mapDemographics(dto: LiveDemographicsRead | undefined): DemographicsViewModel {
  if (dto === undefined) {
    return {
      firstName: null,
      lastName: null,
      lastNameAtBirth: null,
      dateOfBirth: null,
      dateOfDeath: null,
      sex: null,
      genderIdentity: null,
      lifeStatus: null,
      causeOfDeath: null,
      aliveAndWell: null,
      pregnancyDatingAnchor: null,
      pregnancyDatingBasis: null,
      pregnancyDatingAsOf: null,
      primaryCareProvider: null,
      referringProvider: null,
      interpreterRequired: null,
      interpreterLanguage: null,
      contactPoints: [],
      addresses: [],
      ethnicities: [],
      physicalChart: null,
    };
  }

  const contactPoints: ContactPointViewModel[] = [];
  for (const raw of dto.contacts ?? []) {
    const mapped = mapContactPoint(raw);
    if (mapped !== null) contactPoints.push(mapped);
  }

  const addresses: AddressViewModel[] = (dto.addresses ?? []).map(mapAddress);

  const ethnicities: EthnicityViewModel[] = [];
  for (const raw of dto.ethnicities ?? []) {
    const mapped = mapEthnicity(raw);
    if (mapped !== null) ethnicities.push(mapped);
  }

  return {
    firstName: readNamePart(dto, 'firstName'),
    lastName: readNamePart(dto, 'lastName'),
    lastNameAtBirth: readNamePart(dto, 'lastNameAtBirth'),
    dateOfBirth: formatPartialDate(dto.dateOfBirth),
    dateOfDeath: formatPartialDate(dto.dateOfDeath),
    sex: readSex(dto.sex),
    genderIdentity: readGenderIdentity(dto.gender),
    lifeStatus: readLifeStatus(dto.lifeStatus),
    causeOfDeath: nullableString(dto.lifeStatus?.causeOfDeath),
    aliveAndWell: dto.lifeStatus?.aliveAndWell ?? null,
    pregnancyDatingAnchor: nullableString(dto.lifeStatus?.pregnancyDatingAnchor),
    pregnancyDatingBasis: narrowEnum<PregnancyDatingBasis>(
      dto.lifeStatus?.pregnancyDatingBasis,
      KNOWN_PREGNANCY_DATING_BASIS,
      null,
    ),
    pregnancyDatingAsOf: nullableString(dto.lifeStatus?.pregnancyDatingAsOf),
    primaryCareProvider: nullableString(dto.primaryCareProvider),
    referringProvider: nullableString(dto.referringProvider),
    interpreterRequired: narrowEnum<InterpreterRequired>(
      dto.interpreterRequired,
      KNOWN_INTERPRETER,
      null,
    ),
    interpreterLanguage: nullableString(dto.interpreterLanguage),
    contactPoints,
    addresses,
    ethnicities,
    physicalChart: mapPhysicalChart(dto.physical_chart),
  };
}

function mapCareTeamMember(
  dto: NonNullable<PersonMetadataDto['care_team']>[number],
): CareTeamMemberViewModel | null {
  const guid = nullableString(dto.guid);
  const name = nullableString(dto.name);
  if (guid === null || name === null) return null;
  return { guid, name, role: nullableString(dto.role) };
}

function mapMetadata(dto: PersonMetadataDto | undefined): PatientMetadataViewModel {
  if (dto === undefined) {
    return {
      program: null,
      assignedGc: null,
      careTeam: [],
      referralSource: null,
      lastInteractedAt: null,
      createdAt: null,
      updatedAt: null,
    };
  }
  const careTeam: CareTeamMemberViewModel[] = [];
  for (const raw of dto.care_team ?? []) {
    const mapped = mapCareTeamMember(raw);
    if (mapped !== null) careTeam.push(mapped);
  }
  return {
    program: nullableString(dto.program),
    assignedGc: nullableString(dto.assigned_gc),
    careTeam,
    referralSource: nullableString(dto.referral_source),
    lastInteractedAt: nullableString(dto.last_interacted_at),
    createdAt: nullableString(dto.created_at),
    updatedAt: nullableString(dto.updated_at),
  };
}

/**
 * Sensitivity block landed with PTV2-1182 and isn't in the checked-in
 * OpenAPI snapshot yet; widen the mapper input so the read stays typed.
 * Accepts both wire casings (snake_case set_by/set_at is what the BE
 * currently emits under Jackson SNAKE_CASE; camelCase kept as a fallback
 * for the drift the OpenAPI export suggested).
 */
interface LiveSensitivityRead {
  readonly flagged?: boolean | null;
  readonly reason?: string | null;
  readonly setBy?: string | null;
  readonly setAt?: string | null;
  readonly set_by?: string | null;
  readonly set_at?: string | null;
}

const EMPTY_SENSITIVITY: SensitivityViewModel = {
  flagged: false,
  reason: null,
  setBy: null,
  setAt: null,
};

function mapSensitivity(raw: LiveSensitivityRead | null | undefined): SensitivityViewModel {
  if (raw === null || raw === undefined) return EMPTY_SENSITIVITY;
  return {
    flagged: raw.flagged === true,
    reason: nullableString(raw.reason),
    setBy: nullableString(raw.setBy ?? raw.set_by),
    setAt: nullableString(raw.setAt ?? raw.set_at),
  };
}

type PersonDtoWithSensitivity = PersonDto & {
  readonly sensitivity?: LiveSensitivityRead | null;
};

/** Maps a `Person` DTO to the view model consumed by `PatientHeader` + `DemographicsSection`. */
export function mapPatient(dto: PersonDto): PatientViewModel {
  const identifiers: PersonIdentifierViewModel[] = [];
  for (const raw of dto.personIdentifiers ?? []) {
    const mapped = mapIdentifier(raw);
    if (mapped !== null) identifiers.push(mapped);
  }
  return {
    guid: dto.id,
    // The person `type` on the wire is the backend PersonType enum (PATIENT / INDIVIDUAL). Only
    // PATIENT is a patient record; everything else (INDIVIDUAL, and the contract's FAMILY_MEMBER
    // alias) is a pedigree-only family member. Compare the raw value rather than narrowEnum, whose
    // fallback would mis-map INDIVIDUAL to PATIENT.
    personType: dto.type === 'PATIENT' ? 'PATIENT' : 'FAMILY_MEMBER',
    // The wire payload includes PTV2-1064 additions that the checked-in schema
    // doesn't surface yet (ethnicities, physical_chart, four scalars). Widen
    // here so the mapper can read them; the strict OpenAPI shape stays the
    // baseline at all other type sites.
    demographics: mapDemographics(dto.demographics as LiveDemographicsRead | undefined),
    identifiers,
    metadata: mapMetadata(dto.metadata),
    sensitivity: mapSensitivity((dto as PersonDtoWithSensitivity).sensitivity),
    pedigreeAttributes: mapPedigreeAttributes(dto),
  };
}

/**
 * The pedigree-display glyph scalars live on the person (camelCase wire, PTV2-1157/1584) but aren't
 * in the checked-in OpenAPI snapshot — read them off the widened dto. Unknown enum values coerce to
 * null so a bad wire value never breaks the record.
 */
function mapPedigreeAttributes(dto: PersonDto): PatientViewModel['pedigreeAttributes'] {
  const g = dto as {
    carrierStatus?: string | null;
    adoptedStatus?: string | null;
    childlessStatus?: string | null;
    childlessReason?: string | null;
    evaluated?: boolean | null;
  };
  return {
    carrierStatus: narrowEnum<CarrierStatus>(g.carrierStatus, KNOWN_CARRIER_STATUS, null),
    adoptedStatus: narrowEnum<AdoptedStatus>(g.adoptedStatus, KNOWN_ADOPTED_STATUS, null),
    childlessStatus: narrowEnum<ChildlessStatus>(g.childlessStatus, KNOWN_CHILDLESS_STATUS, null),
    childlessReason: nullableString(g.childlessReason),
    evaluated: typeof g.evaluated === 'boolean' ? g.evaluated : null,
  };
}

const KNOWN_CARRIER_STATUS: ReadonlySet<CarrierStatus> = new Set<CarrierStatus>([
  'CARRIER',
  'UNCERTAIN',
  'PRESYMPTOMATIC',
  'AFFECTED',
]);
const KNOWN_ADOPTED_STATUS: ReadonlySet<AdoptedStatus> = new Set<AdoptedStatus>(['ADOPTED_IN', 'ADOPTED_OUT']);
const KNOWN_CHILDLESS_STATUS: ReadonlySet<ChildlessStatus> = new Set<ChildlessStatus>(['CHILDLESS', 'INFERTILE']);

function pickMrnFromPersonIdentifierViewModels(
  identifiers: readonly PersonIdentifierViewModel[],
): string | null {
  const mrn = identifiers.find((id) => id.identifierType.toUpperCase() === 'MRN');
  if (mrn === undefined) return null;
  return nullableString(mrn.value);
}

/**
 * Maps one `Person` row from `GET /persons` into the lightweight summary shape
 * that feeds the Quick Search index.
 *
 * All person types are included — NOT just `PATIENT`. Browse Patients
 * (`/recent-patients`) surfaces family members as navigable rows, so the Quick
 * Search index must too; filtering to `PATIENT` here made records that appear
 * in Browse invisible to Quick Search (PTV2-1232). The only row dropped is one
 * missing a `guid`, which we can't link to or key on.
 */
export function mapPersonToPatientSummary(dto: PersonDto): PatientSummaryViewModel | null {
  if (typeof dto.id !== 'string' || dto.id.length === 0) return null;
  const vm = mapPatient(dto);
  return {
    guid: vm.guid,
    firstName: vm.demographics.firstName,
    lastName: vm.demographics.lastName,
    dateOfBirth: vm.demographics.dateOfBirth,
    sex: vm.demographics.sex,
    primaryIdentifier: pickMrnFromPersonIdentifierViewModels(vm.identifiers),
    program: vm.metadata.program,
    assignedGc: vm.metadata.assignedGc,
    lastInteractedAt: vm.metadata.lastInteractedAt,
    sensitive: vm.sensitivity.flagged,
  };
}

/** Maps a page `content` array from `GET /persons` to patient summaries. */
export function mapPersonPageContentToPatientSummaries(
  rows: readonly PersonDto[],
): PatientSummaryViewModel[] {
  const out: PatientSummaryViewModel[] = [];
  for (const row of rows) {
    const mapped = mapPersonToPatientSummary(row);
    if (mapped !== null) out.push(mapped);
  }
  return out;
}

/**
 * Maps a `PersonSummary` DTO. Returns ``null`` when the server omits the
 * ``guid`` — the UI can't link to or key on a summary without one, so we
 * drop it at the boundary rather than bury the defect downstream.
 */
export function mapPatientSummary(dto: PersonSummaryDto): PatientSummaryViewModel | null {
  if (dto.guid === undefined || dto.guid.length === 0) return null;
  return {
    guid: dto.guid,
    firstName: nullableString(dto.first_name),
    lastName: nullableString(dto.last_name),
    dateOfBirth: nullableString(dto.date_of_birth),
    sex: narrowEnum<SexCode>(dto.sex, KNOWN_SEX, null),
    primaryIdentifier: pickPrimaryIdentifier(dto.identifiers),
    program: nullableString(dto.program),
    assignedGc: nullableString(dto.assigned_gc),
    lastInteractedAt: coerceTimestamp(dto.last_interacted_at),
    sensitive: readSummarySensitiveFlag(dto),
  };
}

/**
 * The sensitivity flag isn't in the checked-in `PersonSummary` OpenAPI
 * snapshot yet, but the BFF list endpoints emit it (PTV2-1182). Widen the DTO
 * at the boundary and accept both the flat boolean and the nested
 * `sensitivity.flagged` shape — same defensive drift handling as the full
 * `Person` sensitivity read. PHI (reason / audit stamps) is intentionally not
 * read here.
 */
function readSummarySensitiveFlag(dto: PersonSummaryDto): boolean {
  const widened = dto as PersonSummaryDto & {
    readonly sensitive?: boolean | null;
    readonly sensitivity?: { readonly flagged?: boolean | null } | null;
  };
  return widened.sensitive === true || widened.sensitivity?.flagged === true;
}

/**
 * The browse table surfaces the patient's MRN. The live BFF returns the
 * full identifier list on `PersonSummary.identifiers`; we pick only the
 * entry tagged `MRN` (case-insensitive) on `user_created_id` and return
 * `null` otherwise so the table renders an empty-cell dash. We deliberately
 * do NOT fall back to other identifier types — the column's heading is
 * MRN, not "any identifier".
 */
function pickPrimaryIdentifier(
  identifiers: PersonSummaryDto['identifiers'],
): string | null {
  if (!Array.isArray(identifiers) || identifiers.length === 0) return null;
  const mrn = identifiers.find(
    (id) => typeof id.user_created_id === 'string' && id.user_created_id.toUpperCase() === 'MRN',
  );
  return mrn === undefined ? null : nullableString(mrn.value);
}

/**
 * The live BFF returns `last_interacted_at` as Unix epoch seconds
 * (e.g. `1778112561`) on `/recent-patients`, even though the contract
 * declares it as an ISO `date-time` string. We accept either shape and
 * normalise to ISO so downstream consumers (`formatDate`) work uniformly.
 */
function coerceTimestamp(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const ms = value < 1e12 ? value * 1000 : value;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }
  return nullableString(value);
}

/** Maps a list, filtering out malformed summaries without guids. */
export function mapPatientSummaries(
  dtos: readonly PersonSummaryDto[],
): PatientSummaryViewModel[] {
  const out: PatientSummaryViewModel[] = [];
  for (const dto of dtos) {
    const mapped = mapPatientSummary(dto);
    if (mapped !== null) out.push(mapped);
  }
  return out;
}

/**
 * Returns the primary identifier string for display in headers / result
 * rows, or ``null`` when the patient has no identifiers.
 *
 * The current rule is "first identifier in the list" — the backend
 * already returns identifiers in priority order (the ``IdentifierType``
 * resource owns the ``is_primary`` flag and the server sorts on it).
 * When the contract ships ``PersonIdentifier.is_primary`` we promote the
 * heuristic here; the view model stays stable.
 */
export function getPrimaryIdentifier(patient: PatientViewModel): string | null {
  const first = patient.identifiers[0];
  if (first === undefined) return null;
  const typeLabel = first.label ?? first.identifierType;
  return `${typeLabel} - ${first.value}`;
}

/**
 * Friendly short labels for identifier types in compact contexts (header
 * meta line, search result rows). The verbose backend `label` field —
 * "Medical Record No.", "PhenoTips ID" — is fine in a Demographics card,
 * but eats too much space in a one-line identity strip.
 */
const SHORT_TYPE_LABEL: Record<string, string> = {
  MRN: 'MRN',
  PH_ID: 'PhenoTips',
  PHENOTIPS_ID: 'PhenoTips',
  OHIP: 'OHIP',
  HC_NUMBER: 'Health Card',
};

/**
 * Compact representation of an identifier for at-a-glance contexts.
 * Always emits the short label so callers can render explicit
 * "MRN: 00042" / "PhenoTips: P0000042" pairs — the header relies on
 * every ID being labelled the same way regardless of value content.
 *
 * Pass the tenant identifier catalog so a type with no short-label entry
 * falls back to the admin-configured label ("MRN") rather than the internal
 * `userEnteredId` ("fhirId") — PTV2-1543.
 */
export function compactIdentifier(
  identifier: PersonIdentifierViewModel,
  definitions?: readonly IdentifierDefinitionDto[],
): { readonly prefix: string; readonly value: string } {
  const { identifierType, value } = identifier;
  const upperType = identifierType.toUpperCase();
  const prefix =
    SHORT_TYPE_LABEL[upperType] ??
    identifierLabelFor(definitions, identifierType, identifier.label);
  return { prefix, value };
}

/** Convenience: "First Last" or ``null`` when both parts are missing. */
export function getPatientFullName(patient: PatientViewModel): string | null {
  const { firstName, lastName } = patient.demographics;
  return joinName(firstName, lastName);
}

/** Parallel of `getPatientFullName` for the lightweight summary shape. */
export function getPatientSummaryFullName(
  summary: PatientSummaryViewModel,
): string | null {
  return joinName(summary.firstName, summary.lastName);
}

/**
 * Two-character uppercase initials derived from first + last name. Returns
 * a single dash when both parts are empty so the caller can render an
 * avatar fallback without branching on null.
 */
export function patientInitials(
  firstName: string | null,
  lastName: string | null,
): string {
  const first = (firstName ?? '').trim().charAt(0);
  const last = (lastName ?? '').trim().charAt(0);
  const combined = `${first}${last}`.toUpperCase();
  return combined.length > 0 ? combined : '—';
}

function joinName(
  firstName: string | null,
  lastName: string | null,
): string | null {
  const parts = [firstName, lastName].filter(
    (part): part is string => part !== null && part.length > 0,
  );
  return parts.length > 0 ? parts.join(' ') : null;
}
